import { createBrowserHistory } from "history";
import React, { Suspense, useEffect, lazy } from "react";
import { Helmet } from "react-helmet";
import { connect, useDispatch } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter,
  Routes,
  useLocation,
} from "react-router-dom";
import { onFCP, onLCP } from "web-vitals";
import config from "../config";
import { getLoggedInUserAccessToken } from "../helpers/account";
import analytics from "../libs/Analytics/analytics";
import {
  getLoggedInUserDetails,
  logoutUser,
} from "../modules/authentication/actions/auth";
import ChangePassword from "../modules/authentication/components/change-password";
import ForgotPassword from "../modules/authentication/components/forgot-password";
import Login from "../modules/authentication/components/login";
import ResetPassword from "../modules/authentication/components/reset-password";
import SignUpRiverMeadow from "../modules/authentication/components/signup/Signup-RiverMeadow";
import SignUp from "../modules/authentication/components/signup/SignupV1";
import VerifyEmail from "../modules/authentication/components/verify-email/VerifyEmail";
import VerifyMFA from "../modules/authentication/components/verify-mfa/VerifyMFA";
import { getAllOrganizations } from "../modules/first-run/actions/firstRun";
import InstanceRemoteSession from "../modules/instance/components/instances/InstanceRemoteSession";
import InstanceRemoteSessionHistory from "../modules/instance/components/instances/InstanceRemoteSessionHistory";
import { getCustomerPreference } from "../modules/new-first-run/actions/new-first-run";
import Day2FeatureSelectionPage from "../modules/new-first-run/components/Day2FeatureSelectionPage";
import NewFirstRun from "../modules/new-first-run/components/NewFirstRun";
import { getRegions } from "../modules/region/actions/regions";
import { getUserData } from "../modules/user-data/actions/user-data";
import SetupMFA from "../modules/users-roles/components/mfa/SetupMFA";
import "../styles/app.scss";
import "../styles/theme.scss";
import { APP_FIRST_RUN, CUSTOMER_ROLE_TYPE } from "../utils/app-constants.json";
import { FEATURE_SELECTION, SETUP_MFA_PATH } from "../utils/app-paths.json";
import { isArrayWithLength } from "../utils/array-methods";
import { getInitialPage } from "../utils/menu-items";
import Loader from "./Loader/Loader";
const LayoutComponent = lazy(() =>
  import("../components/Layout" /* webpackChunkName: "layout" */)
);

function RequireAuth({ children, redirectTo = "/login" }) {
  let isUserLoggedIn = getLoggedInUserAccessToken();
  const location = useLocation();
  const dispatch = useDispatch();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.key]);

  if (!isUserLoggedIn) {
    dispatch(logoutUser());
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}

const history = createBrowserHistory();
const accessType = localStorage.getItem(CUSTOMER_ROLE_TYPE);

const initialPage = getInitialPage(accessType);
const isDemoMode = config.demoApp ? config.demoApp.trim() === "true" : false;

const sendToAnalytics = (data) => {
  analytics.track({
    eventName: data.name,
    additionalProps: data,
  });
};

class App extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isFirstRender: true,
      isUserLoggedIn: getLoggedInUserAccessToken(),
      isAnalyticsInitialized: false,
      fcpData: null,
      lcpData: null,
    };
  }

  componentDidMount() {
    const isFirstRunUrl = history.location.pathname === APP_FIRST_RUN;
    if (this.state.isUserLoggedIn && isFirstRunUrl) {
      this.props.getUserData();
      this.props.getRegions();
      this.props.getLoggedInUserDetails();
      this.props.getCustomerPreference();
    }
    onFCP((data) => this.setState({ fcpData: data }));
    onLCP((data) => this.setState({ lcpData: data }));
    history.listen((location, action) => {
      const url = `${location.pathname}${location.search}`;
      analytics.trackPage(url);
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.userId && prevProps.userId !== this.props.userId) {
      analytics.updateUser(this.props.userId);
      this.setState({ isAnalyticsInitialized: true });
    }

    if (this.state.isAnalyticsInitialized) {
      if (this.state.fcpData) {
        sendToAnalytics(this.state.fcpData);
        this.setState({ fcpData: null });
      }
      if (this.state.lcpData) {
        sendToAnalytics(this.state.lcpData);
        this.setState({ lcpData: null });
      }
    }
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.refreshPage) {
      window.location.reload();
    }
  }

  render() {
    const isFirstRunUrl = history.location.pathname === APP_FIRST_RUN;
    const { isFirstRender, isUserLoggedIn } = this.state;
    const {
      filteredUserData,
      filteredUserDataLoaded,
      subscriptionDetails,
      customerPreferencesFetched,
      accountList,
      azureSubscriptionsList,
    } = this.props;
    if (
      isFirstRender &&
      isUserLoggedIn &&
      filteredUserDataLoaded &&
      filteredUserData !== null &&
      subscriptionDetails !== null &&
      customerPreferencesFetched
    ) {
      this.setState({ isFirstRender: false });

      if (
        isFirstRunUrl &&
        filteredUserData &&
        (isArrayWithLength(accountList) ||
          (!isArrayWithLength(accountList) &&
            isArrayWithLength(filteredUserData.Departments) &&
            filteredUserData.Departments.length > 1) ||
          isArrayWithLength(azureSubscriptionsList)) &&
        !isDemoMode
      ) {
        history.push(initialPage);
      }
    }

    if (
      isUserLoggedIn &&
      isFirstRunUrl &&
      isFirstRender &&
      !customerPreferencesFetched
    ) {
      return (
        <div className="loading">
          <i className="fa fa-spin fa-spinner" />
        </div>
      );
    }
    return (
      <React.Fragment>
        <Helmet>
          <title>MontyCloud DAY2™</title>
        </Helmet>
        <BrowserRouter>
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route
                path="/app/all-resources/compute/server/:instanceId/remote-session/:sessionId"
                element={
                  <RequireAuth redirectTo="/login">
                    <InstanceRemoteSessionHistory
                      dispatch={this.props.dispatch}
                    />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/all-resources/compute/server/:instanceId/remote-session"
                element={
                  <RequireAuth redirectTo="/login">
                    <InstanceRemoteSession dispatch={this.props.dispatch} />
                  </RequireAuth>
                }
              />
              <Route
                path="/app/first-run"
                element={
                  <RequireAuth redirectTo="/login">
                    <NewFirstRun />
                  </RequireAuth>
                }
              />
              <Route
                path={FEATURE_SELECTION}
                element={
                  <RequireAuth redirectTo="/login">
                    <Day2FeatureSelectionPage dispatch={this.props.dispatch} />
                  </RequireAuth>
                }
              />
              <Route
                path={SETUP_MFA_PATH}
                element={
                  <RequireAuth redirectTo="/login">
                    <SetupMFA dispatch={this.props.dispatch} />
                  </RequireAuth>
                }
              />
              <Route path="/" element={<Navigate to={initialPage} />} />
              <Route path="/app" element={<Navigate to={initialPage} />} />
              <Route
                path="/app/*"
                element={
                  <RequireAuth redirectTo="/login">
                    <LayoutComponent dispatch={this.props.dispatch} />
                  </RequireAuth>
                }
              />
              <Route
                path="/documentation"
                element={
                  <Navigate to="/documentation/getting-started/overview" />
                }
              />
              <Route path="/login" element={<Login />} />
              <Route path="/verify-mfa" element={<VerifyMFA />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/rm-signup" element={<SignUpRiverMeadow />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to={initialPage} />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  isAuthenticated: state.tokenReducer.isAuthenticated,
  isMFAEnabled: state.auth.isMFAEnabled,
  isMFAConfigured: state.auth.isMFAConfigured,
  refreshPage: state.auth.refreshPage,
  organizations: state.globalConfig.organizations,
  organizationsLoaded: state.globalConfig.organizationsLoaded,
  filteredUserData: state.globalConfig.filteredUserData,
  filteredUserDataLoaded: state.globalConfig.filteredUserDataLoaded,
  subscriptionDetails: state.auth.subscriptionDetails,
  customerPreferencesFetched: state.newFirstRun.customerPreferencesFetched,
  accountList: state.accounts.accountList,
  azureSubscriptionsList: state.accounts.azureSubscriptionsList,
  userId: state.auth.userId,
});

const mapDispatchToProps = (dispatch) => {
  return {
    getUserData: () => dispatch(getUserData()),
    getRegions: () => dispatch(getRegions()),
    getAllOrganizations: () => dispatch(getAllOrganizations()),
    getLoggedInUserDetails: () => dispatch(getLoggedInUserDetails()),
    getCustomerPreference: () => dispatch(getCustomerPreference()),
    dispatch,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
